import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { FixedValue, FixedValueListResponse, FixedValueCreate, FixedValueUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10; // Define page size for consistency

interface UseFixedValuesResult {
  fixedValues: FixedValue[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchFixedValues: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createFixedValue: (data: FixedValueCreate) => Promise<boolean>;
  updateFixedValue: (id: number, data: FixedValueUpdate) => Promise<boolean>;
  deleteFixedValue: (id: number) => Promise<boolean>;
}

export const useFixedValues = (): UseFixedValuesResult => {
  const [fixedValues, setFixedValues] = useState<FixedValue[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFixedValues = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<FixedValueListResponse>(
        `/fixed_values?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setFixedValues(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch fixed values: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching fixed values.');
      }
      setFixedValues([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchFixedValues(currentPage);
  }, [currentPage, fetchFixedValues]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createFixedValue = async (data: FixedValueCreate): Promise<boolean> => {
    setError(null);
    try {
      // Ensure cost is a number
      const payload = { ...data, cost: parseFloat(data.cost as any) };
      await apiClient<FixedValue>('/fixed_values', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      await fetchFixedValues(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create fixed value: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateFixedValue = async (id: number, data: FixedValueUpdate): Promise<boolean> => {
    setError(null);
    try {
      // Ensure cost is a number if present
      const payload = { ...data };
      if (payload.cost !== undefined) {
        payload.cost = parseFloat(payload.cost as any);
      }
      await apiClient<FixedValue>(`/fixed_values/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchFixedValues(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update fixed value: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteFixedValue = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Fixed Value ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/fixed_values/${id}`, { method: 'DELETE' });
      const pageToFetch = fixedValues.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchFixedValues(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete fixed value: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    fixedValues,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchFixedValues,
    handlePageChange,
    createFixedValue,
    updateFixedValue,
    deleteFixedValue,
  };
};